import { nomAffiche } from '../format/designation';
import { Pastille, type TeinteDeStatut } from './Pastille';

/**
 * **Une personne dans une liste : son PRÉNOM et son état de présence, rien d'autre.**
 *
 * `CDC-3` F-42 et `CDC-4` F-50 : « **le produit ne montre jamais d'un tiers plus que son
 * prénom** ». Et « **il n'y a pas de fiche d'une personne** : une ligne, et rien
 * derrière — le produit ne montre jamais d'un tiers plus que son prénom, donc une fiche
 * n'aurait rien à porter ». Ce composant ne prend donc **ni nom, ni adresse, ni rôle**, et
 * il n'ouvre rien.
 *
 * **Les trois valeurs de présence, et il n'y en a pas d'autre** (F-43) : absent, connecté,
 * occupé. « **Personne ne peut se rendre invisible** : aucun mode indisponible, aucun
 * masquage, aucune exception. »
 *
 * **Un compte anonymisé sort SANS NOM**, et l'écran ne dit ni la cause ni la date : « une
 * date dirait qu'un compte a été supprimé ce jour-là ».
 */
export type EtatDePresence = 'absent' | 'connecte' | 'occupe';

const TEINTE: Record<EtatDePresence, TeinteDeStatut> = {
  connecte: 'positif',
  occupe: 'attention',
  absent: 'eteint',
};

const MOT: Record<EtatDePresence, string> = {
  connecte: 'en ligne',
  occupe: 'en cours',
  absent: 'absent',
};

type Props = {
  readonly prenom: string | null;
  readonly etat: EtatDePresence;
  /** L'action de la ligne. La liste des gestes est fermée par le cahier des charges. */
  readonly action?: React.ReactNode;
};

export function LigneDePersonne({ prenom, etat, action }: Props) {
  return (
    <li className="flex min-h-cible items-center justify-between gap-4 border-b border-bord px-1 py-2 last:border-b-0">
      <span className="flex items-center gap-2">
        {/* Pleine quand la personne est là, creuse quand rien n'a lieu — la forme
            porte l'information autant que la teinte. */}
        <Pastille teinte={TEINTE[etat]} pleine={etat !== 'absent'} />
        <span className="text-16 font-weight-500 text-texte-fort">
          {nomAffiche(prenom)}
        </span>
        <span className="text-13 text-texte-faible">{MOT[etat]}</span>
      </span>
      {action}
    </li>
  );
}
